import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle } from 'react-icons/fi';
import Modal from './Modal';
import Button from './Button';

const ICON_TONES = {
  red: 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400',
  blue: 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400',
  green: 'bg-green-100 text-green-600 dark:bg-green-500/15 dark:text-green-400',
  amber: 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
};

/**
 * Drop-in replacement for the legacy ConfirmationModal — identical props:
 * isOpen, onClose, onConfirm, title, message, confirmText, cancelText, confirmColor.
 *
 * Self-manages async confirm: sets internal `busy` while onConfirm() is pending,
 * auto-closes on success, stays open on error (caller's catch normally toasts).
 * External `loading` prop still accepted for backwards compatibility.
 */
export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  confirmColor = 'red',
  loading = false,
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const isLoading = loading || busy;

  const titleText = title ?? t('cm.confirmAction');
  const messageText = message ?? t('cm.confirmProceed');
  const confirmLabel = confirmText ?? t('appt.actions.confirm');
  const cancelLabel = cancelText ?? t('appt.actions.cancel');

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await Promise.resolve(onConfirm());
      onClose();
    } catch {
      // Error already handled by caller (toast, etc.); keep dialog open.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" dismissible={!isLoading}>
      <div className="text-center">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${ICON_TONES[confirmColor] || ICON_TONES.red}`}>
          <FiAlertTriangle size={22} />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-content">{titleText}</h3>
        {/* A string is prose; anything else is already markup (a message
            preview, a list of what will change) and must not be nested inside
            a <p>, which is invalid HTML the browser silently re-parents. */}
        {typeof messageText === 'string' ? (
          <p className="mt-2 text-sm text-content-secondary">{messageText}</p>
        ) : (
          <div className="mt-2 text-sm text-content-secondary">{messageText}</div>
        )}
        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-center">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            variant={confirmColor === 'red' ? 'danger' : confirmColor === 'green' ? 'success' : 'primary'}
            className={confirmColor === 'amber' ? '!bg-amber-600 hover:!bg-amber-700 !text-white' : ''}
            onClick={handleConfirm}
            loading={isLoading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
